// src/pages/admin/ManagerList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ExcelDownloadButton from '../../components/common/ExcelDownloadButton';
import { getUserListExportColumns } from '../../utils/userListExport';
import API from '../../api';
import UserLink from '../../components/common/UserLink';
import UserRowActions from '../../components/common/UserRowActions';
import UserStatusBadge from '../../components/common/UserStatusBadge';
import { formatFullName } from '../../utils/name';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../components/layout/ListPageLayout';
import PaginationFooter from '../../components/common/PaginationFooter';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import ListRecordIdBadge from '../../components/common/ListRecordIdBadge';
import useSortedClientPagination from '../../hooks/useSortedClientPagination';
import { userListSortColumns } from '../../config/tableSortColumns';
import { resolveRowId } from '../../utils/tableSort';
// tiny debounce to keep typing smooth
function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const ManagerList = () => {
    const { t } = useTranslation();
    const [managers, setManagers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);
    const [filter, setFilter] = useState('active'); // all, active, archived
    const navigate = useNavigate();

    const loadManagers = () => {
        setLoading(true);
        API.get('/admin/users', { params: { role: 'MANAGER' } })
            .then(response => {
                setManagers(response.data);
                setLoadError(false);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching managers:', error);
                setManagers([]);
                setLoadError(true);
                setLoading(false);
            });
    };

    useEffect(() => {
        loadManagers();
    }, []);

    const filteredManagers = useMemo(() => {
        let result = managers || [];

        // Apply filter tabs
        if (filter === 'active') {
            result = result.filter(m => m.active !== false);
        } else if (filter === 'archived') {
            result = result.filter(m => m.active === false);
        }
        // 'all' shows everything

        // Apply search query
        const term = (dq || '').trim().toLowerCase();
        if (term) {
            const pick = (x) => (x ?? '').toString().toLowerCase();
            result = result.filter(m =>
                [
                    m.firstName, m.middleName, m.lastName,
                    m.username || m.userName,
                    m.email,
                    m.phoneNumber || m.phone,
                    m.id || m.userId || m.user_id
                ].some(v => pick(v).includes(term))
            );
        }

        return result;
    }, [managers, dq, filter]);

    const pagination = useSortedClientPagination(filteredManagers, {
        columns: userListSortColumns,
        resetKey: `${filter}-${dq}`,
    });

    const shown = filteredManagers.length;
    const total = filter === 'all' ? managers.length : filter === 'active' ? managers.filter(m => m.active !== false).length : managers.filter(m => m.active === false).length;

    const managerFilterTabs = [
        { key: 'all', label: t('common.all'), count: managers.length },
        { key: 'active', label: t('common.active'), count: managers.filter(m => m.active !== false).length },
        { key: 'archived', label: t('common.archived'), count: managers.filter(m => m.active === false).length },
    ];

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.managers.title')}
                subtitle={t('pages.managers.subtitle')}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={getUserListExportColumns(t)}
                            data={filteredManagers}
                            filename="managers"
                            disabled={filteredManagers.length === 0}
                        />
                        <button
                            onClick={() => navigate('/admin/managers/new')}
                            className="btn-page-action-primary"
                        >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('pages.managers.add')}
                    </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={managerFilterTabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.managers.searchPlaceholder')}
                shown={shown}
                total={total}
                entityLabel={t('pages.managers.entityLabel')}
            />

            {loadError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.listLoadFailed')}
                </div>
            )}
            {loading ? (
                <ListLoadingSpinner />
            ) : loadError ? null : filteredManagers.length === 0 ? (
                <ListEmptyState
                    message={q ? t('common.noMatchSearch', { entity: t('pages.managers.entityLabel') }) : t('common.noFound', { entity: t('pages.managers.entityLabel') }) }
                    hint={q ? t('common.emptySearchHint') : undefined}
                />
            ) : (
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t('common.idColumn')} columnKey="id" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('pages.managers.colManagerName')} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.username')} columnKey="username" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.email')} columnKey="email" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.phone')} columnKey="phone" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <th className="table-header-cell">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map(manager => (
                                    <tr key={String(manager.id ?? manager.user_id)} className="table-row">
                                        <td className="table-cell whitespace-nowrap">
                                            <ListRecordIdBadge id={resolveRowId(manager)} />
                                        </td>
                                        <td className="table-cell">
                                            {formatFullName(manager.firstName, manager.middleName, manager.lastName) ? (
                                                <UserLink
                                                    id={manager.id}
                                                    name={formatFullName(manager.firstName, manager.middleName, manager.lastName)}
                                                />
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="table-cell">{manager.username || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{manager.email || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{manager.phoneNumber || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">
                                            <UserStatusBadge active={manager.active} />
                                        </td>
                                        <td className="table-cell whitespace-nowrap">
                                            <UserRowActions
                                                user={manager}
                                                entityLabel="manager"
                                                editPath={`/admin/managers/${manager.id}`}
                                                onStatusChange={loadManagers}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </ListTableCard>
            )}
        </ListPageLayout>
    );
};

export default ManagerList;
